import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Check, X, ExternalLink, RefreshCw, ShieldAlert, Filter, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const STATUS_COLORS = {
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminDiscoveries() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (category !== "all") params.category = category;
      const { data } = await api.get("/ingest/pending-discoveries", { params });
      setCandidates(data.candidates || []);
      setSelected(new Set());
    } catch (e) {
      const msg = e?.response?.status === 403 ? "Admin access required" : "Failed to load candidates";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryCounts = useMemo(() => {
    const m = new Map();
    candidates.forEach((c) => {
      const k = c.category_guess || "Uncategorized";
      m.set(k, (m.get(k) || 0) + 1);
    });
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [candidates]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.id)));
  };

  const promote = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ingest/pending-discoveries/${id}/promote`);
      if (data.ok) {
        toast.success("Promoted · Firecrawl enrichment fetched" + (data.enrichment?.tagline ? " ✓" : ""));
        setCandidates((prev) => prev.filter((c) => c.id !== id));
      }
    } catch (e) {
      toast.error("Promote failed");
    } finally { setBusy(false); }
  };

  const reject = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ingest/pending-discoveries/${id}/reject`);
      if (data.ok) {
        toast.success("Rejected");
        setCandidates((prev) => prev.filter((c) => c.id !== id));
      }
    } catch (e) {
      toast.error("Reject failed");
    } finally { setBusy(false); }
  };

  const bulk = async (action) => {
    if (selected.size === 0) return;
    if (!window.confirm(`${action === "promote" ? "Approve" : "Reject"} ${selected.size} candidate${selected.size === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/ingest/pending-discoveries/bulk", { ids, action });
      toast.success(`${action === "promote" ? "Approved" : "Rejected"} ${data.modified} candidate${data.modified === 1 ? "" : "s"}`);
      setCandidates((prev) => prev.filter((c) => !selected.has(c.id)));
      setSelected(new Set());
    } catch (e) {
      toast.error("Bulk action failed");
    } finally { setBusy(false); }
  };

  const runWeekly = async () => {
    if (!window.confirm("Run the discovery pipeline now? This scrapes GitHub, Product Hunt, and Firecrawl hubs — may take 1-2 minutes.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/ingest/run-weekly");
      const total = Object.values(data || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
      toast.success(`Discovery complete · ${total} candidates surfaced`);
      await load();
    } catch (e) {
      toast.error("Discovery run failed");
    } finally { setRunning(false); }
  };

  if (!user) {
    return (
      <Layout hideRightPanel>
        <PageHero eyebrow="Admin" title={<>Pending <span className="italic">discoveries</span></>} sub="Sign in as an admin to review the auto-discovered tool queue." />
        <div className="curio-card p-6 text-sm text-[#3D465C]" data-testid="admin-signin-cta">Please <Link to="/login" className="text-[var(--curio-coral)] font-semibold">sign in</Link> first.</div>
      </Layout>
    );
  }
  if (!user.is_admin) {
    return (
      <Layout hideRightPanel>
        <PageHero eyebrow="Admin" title={<>Pending <span className="italic">discoveries</span></>} sub="This area is restricted to admins." />
        <div className="curio-card p-6 flex items-center gap-3 text-sm text-[#3D465C]" data-testid="admin-forbidden">
          <ShieldAlert className="text-rose-500" size={18} />
          Your account (<span className="font-mono">{user.email}</span>) is not on the admin allowlist.
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideRightPanel>
      <PageHero
        eyebrow="Admin · Ingestion"
        title={<>Pending <span className="italic">discoveries</span></>}
        sub="Review, approve, or reject tools surfaced by the automated discovery pipeline. Approved candidates flow into the enrichment queue."
        badge={`${candidates.length} in queue`}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="admin-toolbar">
        <button
          onClick={runWeekly}
          disabled={running || busy}
          className="inline-flex items-center gap-2 rounded-md bg-[#0B1220] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          data-testid="admin-run-weekly-btn"
        >
          {running ? <RefreshCw size={14} className="animate-spin"/> : <PlayCircle size={14}/>}
          {running ? "Discovering…" : "Run discovery now"}
        </button>
        <button
          onClick={load}
          disabled={loading || busy}
          className="inline-flex items-center gap-2 rounded-md bg-white border border-[#E7EAF0] px-3 py-2 text-sm font-semibold text-[#0B1220] hover:bg-[#F7F5F2] disabled:opacity-50"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
        <div className="flex-1" />
        <button
          onClick={() => bulk("promote")}
          disabled={selected.size === 0 || busy}
          className="inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white px-3 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
          data-testid="admin-bulk-promote-btn"
        >
          <Check size={14}/> Approve selected ({selected.size})
        </button>
        <button
          onClick={() => bulk("reject")}
          disabled={selected.size === 0 || busy}
          className="inline-flex items-center gap-2 rounded-md bg-rose-600 text-white px-3 py-2 text-sm font-semibold hover:bg-rose-700 disabled:opacity-40"
          data-testid="admin-bulk-reject-btn"
        >
          <X size={14}/> Reject selected ({selected.size})
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="admin-cat-filters">
        <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8A93A6] mr-2">
          <Filter size={11}/> Category
        </span>
        <button
          onClick={() => setCategory("all")}
          className={`text-xs px-2.5 py-1 rounded-md border ${category === "all" ? "bg-[#0B1220] text-white border-[#0B1220]" : "bg-white text-[#3D465C] border-[#E7EAF0] hover:bg-[#F7F5F2]"}`}
          data-testid="admin-cat-all"
        >
          All ({candidates.length})
        </button>
        {categoryCounts.map(([k, n]) => (
          <button
            key={k}
            onClick={() => setCategory(k === "Uncategorized" ? "all" : k)}
            className={`text-xs px-2.5 py-1 rounded-md border ${category === k ? "bg-[#0B1220] text-white border-[#0B1220]" : "bg-white text-[#3D465C] border-[#E7EAF0] hover:bg-[#F7F5F2]"}`}
            data-testid={`admin-cat-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {k} ({n})
          </button>
        ))}
      </div>

      <div className="rounded-lg bg-white border border-[#E7EAF0] overflow-hidden" data-testid="admin-table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5F2] border-b border-[#E7EAF0]">
            <tr className="text-left">
              <th className="p-3 w-8">
                <input
                  type="checkbox"
                  checked={candidates.length > 0 && selected.size === candidates.length}
                  onChange={toggleAll}
                  data-testid="admin-select-all"
                />
              </th>
              <th className="p-3 font-semibold text-[#0B1220]">Name</th>
              <th className="p-3 font-semibold text-[#0B1220]">URL</th>
              <th className="p-3 font-semibold text-[#0B1220]">Category</th>
              <th className="p-3 font-semibold text-[#0B1220]">Source</th>
              <th className="p-3 font-semibold text-[#0B1220]">Discovered</th>
              <th className="p-3 font-semibold text-[#0B1220] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-8 text-center text-[#8A93A6]">Loading…</td></tr>
            )}
            {!loading && candidates.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#8A93A6]" data-testid="admin-empty">
                No pending discoveries. Run the pipeline to surface new candidates.
              </td></tr>
            )}
            {candidates.map((c) => {
              const checked = selected.has(c.id);
              const dt = c.discovered_at ? new Date(c.discovered_at).toLocaleDateString() : "—";
              return (
                <tr key={c.id} className={`border-b border-[#F0F1F5] hover:bg-[#FAFBFC] ${checked ? "bg-[#F5F8FF]" : ""}`} data-testid={`admin-row-${c.id}`}>
                  <td className="p-3">
                    <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} data-testid={`admin-select-${c.id}`} />
                  </td>
                  <td className="p-3 font-semibold text-[#0B1220]">{c.name}</td>
                  <td className="p-3">
                    <a href={c.candidate_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#3D465C] hover:text-[var(--curio-coral)]">
                      <span className="max-w-[280px] truncate inline-block align-bottom">{c.domain || c.candidate_url}</span>
                      <ExternalLink size={12}/>
                    </a>
                  </td>
                  <td className="p-3">
                    <span className="inline-block text-[11px] px-2 py-0.5 rounded bg-white border border-[#E7EAF0] text-[#3D465C]">
                      {c.category_guess || "—"}
                    </span>
                  </td>
                  <td className="p-3 text-[11px] text-[#8A93A6]" title={c.source_list}>
                    {(c.source_list || "").replace(/^github:/, "gh:").slice(0, 32)}
                  </td>
                  <td className="p-3 text-[11px] text-[#8A93A6]">{dt}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <span className={`inline-block text-[10px] uppercase tracking-wide px-2 py-0.5 mr-2 rounded border ${STATUS_COLORS[c.status] || ""}`}>{c.status}</span>
                    <button
                      onClick={() => promote(c.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-2.5 py-1 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 mr-1"
                      data-testid={`admin-promote-${c.id}`}
                    >
                      <Check size={12}/> Approve
                    </button>
                    <button
                      onClick={() => reject(c.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md bg-white border border-rose-200 text-rose-700 px-2.5 py-1 text-xs font-semibold hover:bg-rose-50 disabled:opacity-40"
                      data-testid={`admin-reject-${c.id}`}
                    >
                      <X size={12}/> Reject
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-[#8A93A6]" data-testid="admin-footnote">
        Approving triggers a Firecrawl-backed metadata scrape for the tool. Bulk approve skips scraping to stay fast — run individual promote later to enrich metadata.
      </p>
    </Layout>
  );
}
